// src/admin/pages/Deck.jsx
import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Stack, Box, IconButton,
  Switch, FormControlLabel, Snackbar, Alert, Tabs, Tab,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  fetchCardGroups, saveCardGroup, deleteCardGroup,
  fetchCards, saveCard, deleteCard, uploadCardImage,
} from '../lib/adminApi';

const EMPTY_GROUP = { label: '', label_en: '', color: '#67F6FF', order: 0, active: true, requiresPurchase: false };
const EMPTY_CARD = {
  label: '', label_en: '', descrizione_breve_it: '', descrizione_breve_en: '',
  descrizione_lunga_it: '', descrizione_lunga_en: '', suggerimento: '',
  freePreview: false, order: 0, image: '', back: '', image_en: '', back_en: '',
};

function GroupRow({ group, onToggleActive, onEditClick, onDelete }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: group.color }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>{group.label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {group.id} · ordine {group.order}{group.requiresPurchase ? ' · a pagamento' : ''}
        </Typography>
      </Box>
      <Switch size="small" checked={!!group.active} onChange={(e) => onToggleActive(e.target.checked)} />
      <IconButton size="small" onClick={onEditClick} title="Modifica">✏️</IconButton>
      <IconButton size="small" onClick={() => onDelete(group)} title="Elimina">🗑</IconButton>
    </Box>
  );
}

function GroupForm({ value, onChange, onSave, onCancel }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '2px solid #1976d2', borderRadius: 2 }}>
      <TextField size="small" label="Nome gruppo" value={value.label}
        onChange={(e) => onChange({ ...value, label: e.target.value })} />
      <TextField size="small" label="Nome gruppo (EN)" value={value.label_en || ''}
        onChange={(e) => onChange({ ...value, label_en: e.target.value })} />
      <TextField size="small" label="Colore (hex)" value={value.color}
        onChange={(e) => onChange({ ...value, color: e.target.value })} />
      <TextField size="small" type="number" label="Ordine" value={value.order}
        onChange={(e) => onChange({ ...value, order: parseInt(e.target.value, 10) || 0 })} />
      <FormControlLabel control={
        <Switch checked={!!value.requiresPurchase}
          onChange={(e) => onChange({ ...value, requiresPurchase: e.target.checked })} />
      } label="Richiede acquisto (pack a pagamento)" />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={onSave} disabled={!value.label.trim()}>Salva</Button>
        <Button size="small" onClick={onCancel}>Annulla</Button>
      </Box>
    </Box>
  );
}

function CardRow({ card, onEdit, onDelete }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      {card.image && <img src={card.image} alt={card.label} style={{ width: 32, height: 'auto', borderRadius: 4 }} />}
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>{card.label}</Typography>
        <Typography variant="caption" color="text.secondary">
          ordine {card.order}{card.freePreview ? ' · free preview' : ''}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => onEdit(card)} title="Modifica">✏️</IconButton>
      <IconButton size="small" onClick={() => onDelete(card)} title="Elimina">🗑</IconButton>
    </Box>
  );
}

function CardForm({ value, onChange, onSave, onCancel, groupId }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const id = value.id || (value.label || 'nuova-carta').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const suffix = field.startsWith('back') ? '_back' : '';
      const langFolder = field.endsWith('_en') ? 'en' : null;
      const url = await uploadCardImage(file, groupId, id, suffix, langFolder);
      onChange({ ...value, [field]: url });
    } finally {
      setUploading(false);
    }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '2px solid #1976d2', borderRadius: 2 }}>
      <TextField size="small" label="Nome carta" value={value.label}
        onChange={(e) => onChange({ ...value, label: e.target.value })} />
      <TextField size="small" label="Nome carta (EN)" value={value.label_en || ''}
        onChange={(e) => onChange({ ...value, label_en: e.target.value })} />
      <TextField size="small" multiline rows={2} label="Descrizione breve (IT)" value={value.descrizione_breve_it}
        onChange={(e) => onChange({ ...value, descrizione_breve_it: e.target.value })} />
      <TextField size="small" multiline rows={2} label="Descrizione breve (EN)" value={value.descrizione_breve_en}
        onChange={(e) => onChange({ ...value, descrizione_breve_en: e.target.value })} />
      <TextField size="small" multiline rows={4} label="Descrizione lunga (IT)" value={value.descrizione_lunga_it}
        onChange={(e) => onChange({ ...value, descrizione_lunga_it: e.target.value })} />
      <TextField size="small" multiline rows={4} label="Descrizione lunga (EN)" value={value.descrizione_lunga_en}
        onChange={(e) => onChange({ ...value, descrizione_lunga_en: e.target.value })} />
      <TextField size="small" multiline rows={2} label="Suggerimento d'uso" value={value.suggerimento}
        onChange={(e) => onChange({ ...value, suggerimento: e.target.value })} />
      <TextField size="small" type="number" label="Ordine" value={value.order}
        onChange={(e) => onChange({ ...value, order: parseInt(e.target.value, 10) || 0 })} />
      <FormControlLabel control={
        <Switch checked={!!value.freePreview}
          onChange={(e) => onChange({ ...value, freePreview: e.target.checked })} />
      } label="Visibile agli utenti Free (freePreview)" />
      <Button component="label" size="small" variant="outlined" disabled={uploading}>
        {value.image ? 'Sostituisci immagine fronte' : 'Carica immagine fronte'}
        <input type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files[0], 'image')} />
      </Button>
      <Button component="label" size="small" variant="outlined" disabled={uploading}>
        {value.back ? 'Sostituisci immagine retro' : 'Carica immagine retro'}
        <input type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files[0], 'back')} />
      </Button>
      <Button component="label" size="small" variant="outlined" disabled={uploading}>
        {value.image_en ? 'Sostituisci immagine fronte (EN)' : 'Carica immagine fronte (EN)'}
        <input type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files[0], 'image_en')} />
      </Button>
      <Button component="label" size="small" variant="outlined" disabled={uploading}>
        {value.back_en ? 'Sostituisci immagine retro (EN)' : 'Carica immagine retro (EN)'}
        <input type="file" hidden accept="image/*" onChange={(e) => onFile(e.target.files[0], 'back_en')} />
      </Button>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={onSave} disabled={!value.label.trim() || uploading}>Salva</Button>
        <Button size="small" onClick={onCancel}>Annulla</Button>
      </Box>
    </Box>
  );
}

export default function Deck() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0); // 0 = groups, 1 = cards
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [cards, setCards] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupDraft, setNewGroupDraft] = useState(EMPTY_GROUP);
  const [editingCard, setEditingCard] = useState(null);
  const [addingCard, setAddingCard] = useState(false);
  const [newCardDraft, setNewCardDraft] = useState(EMPTY_CARD);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'group'|'card', item }
  const [toast, setToast] = useState(null);

  const loadGroups = async () => setGroups(await fetchCardGroups());
  const loadCards = async (groupId) => setCards(groupId ? await fetchCards(groupId) : []);

  useEffect(() => { loadGroups(); }, []);
  useEffect(() => { loadCards(selectedGroupId); }, [selectedGroupId]);

  const onSaveGroup = async (group) => {
    await saveCardGroup(currentUser, group);
    setEditingGroup(null);
    setAddingGroup(false);
    setNewGroupDraft(EMPTY_GROUP);
    await loadGroups();
    setToast({ severity: 'success', msg: 'Gruppo salvato' });
  };

  const onSaveCard = async (card) => {
    await saveCard(currentUser, selectedGroupId, card);
    setEditingCard(null);
    setAddingCard(false);
    setNewCardDraft(EMPTY_CARD);
    await loadCards(selectedGroupId);
    setToast({ severity: 'success', msg: 'Carta salvata' });
  };

  const onConfirmDelete = async () => {
    if (deleteTarget.type === 'group') {
      await deleteCardGroup(currentUser, deleteTarget.item.id);
      await loadGroups();
      if (selectedGroupId === deleteTarget.item.id) setSelectedGroupId(null);
    } else {
      await deleteCard(currentUser, deleteTarget.item);
      await loadCards(selectedGroupId);
    }
    setDeleteTarget(null);
    setToast({ severity: 'success', msg: 'Eliminato' });
  };

  return (
    <>
      <PageHeader title="Deck di carte" subtitle="Gruppi e carte del deck, sincronizzati in tempo reale con il canvas" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Gruppi" />
        <Tab label="Carte" />
      </Tabs>

      {tab === 0 && (
        <Card><CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Gruppi</Typography>
            <Button size="small" variant="outlined" onClick={() => { setAddingGroup(true); setEditingGroup(null); setNewGroupDraft(EMPTY_GROUP); }} disabled={addingGroup}>
              + Aggiungi gruppo
            </Button>
          </Box>
          <Stack spacing={1}>
            {groups.map((g) => (
              editingGroup?.id === g.id ? (
                <GroupForm key={g.id} value={editingGroup} onChange={setEditingGroup}
                  onSave={() => onSaveGroup(editingGroup)} onCancel={() => setEditingGroup(null)} />
              ) : (
                <GroupRow key={g.id} group={g}
                  onToggleActive={(active) => onSaveGroup({ ...g, active })}
                  onEditClick={() => setEditingGroup(g)}
                  onDelete={(item) => setDeleteTarget({ type: 'group', item })}
                />
              )
            ))}
            {addingGroup && (
              <GroupForm value={newGroupDraft} onChange={setNewGroupDraft} onSave={() => onSaveGroup(newGroupDraft)} onCancel={() => setAddingGroup(false)} />
            )}
          </Stack>
        </CardContent></Card>
      )}

      {tab === 1 && (
        <Card><CardContent>
          <TextField select SelectProps={{ native: true }} size="small" label="Gruppo"
            value={selectedGroupId || ''} onChange={(e) => setSelectedGroupId(e.target.value || null)}
            sx={{ mb: 2, minWidth: 240 }}>
            <option value="">Seleziona un gruppo…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </TextField>
          {selectedGroupId && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Carte</Typography>
                <Button size="small" variant="outlined" onClick={() => { setAddingCard(true); setEditingCard(null); setNewCardDraft({ ...EMPTY_CARD, order: cards.length }); }} disabled={addingCard}>
                  + Aggiungi carta
                </Button>
              </Box>
              <Stack spacing={1}>
                {cards.map((c) => (
                  editingCard?.id === c.id ? (
                    <CardForm key={c.id} value={editingCard} onChange={setEditingCard} groupId={selectedGroupId}
                      onSave={() => onSaveCard(editingCard)} onCancel={() => setEditingCard(null)} />
                  ) : (
                    <CardRow key={c.id} card={c} onEdit={setEditingCard}
                      onDelete={(item) => setDeleteTarget({ type: 'card', item })} />
                  )
                ))}
                {addingCard && (
                  <CardForm value={newCardDraft} onChange={setNewCardDraft} groupId={selectedGroupId}
                    onSave={() => onSaveCard(newCardDraft)} onCancel={() => setAddingCard(false)} />
                )}
              </Stack>
            </>
          )}
        </CardContent></Card>
      )}

      <ConfirmDialog open={!!deleteTarget} title={`Elimina ${deleteTarget?.type === 'group' ? 'gruppo' : 'carta'}`}
        confirmLabel="Elimina" confirmColor="error"
        message={`Eliminare definitivamente "${deleteTarget?.item?.label}"? L'azione non è reversibile.`}
        onConfirm={onConfirmDelete} onClose={() => setDeleteTarget(null)}
      />
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </>
  );
}
